import { useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Menu,
  PasswordInput,
  Stack,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IconDots, IconKey, IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import { DataTable, DataTableColumn } from "@/components/DataTable";
import { FormModal } from "@/components/FormModal";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { api } from "@/lib/api";
import { formatAPIError, qk } from "@/lib/apiHelpers";
import type {
  CreateUserRequest,
  UpdateUserRequest,
  User,
  UsersListResponse,
} from "@/types/api";
import { UserForm, UserFormApi, UserFormValues } from "./UserForm";

export default function UsersPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debounced] = useDebouncedValue(search, 300);

  const filters = { page, page_size: 50, q: debounced };
  const { data, isLoading } = useQuery({
    queryKey: qk.users(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("page", String(filters.page));
      params.set("page_size", String(filters.page_size));
      if (filters.q) params.set("q", filters.q);
      const r = await api.get<UsersListResponse>(`/api/v1/users?${params.toString()}`);
      return r.data;
    },
  });

  // create / edit modal
  const [editingUser, setEditingUser] = useState<User | null | undefined>(undefined);
  const [formApi, setFormApi] = useState<UserFormApi | null>(null);
  const isFormOpen = editingUser !== undefined;

  const createMut = useMutation({
    mutationFn: async (body: CreateUserRequest) =>
      (await api.post<User>("/api/v1/users", body)).data,
    onSuccess: () => {
      notifications.show({ message: "Пользователь создан", color: "green" });
      void qc.invalidateQueries({ queryKey: ["users"] });
      setEditingUser(undefined);
    },
    onError: (e) => notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });
  const updateMut = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: UpdateUserRequest }) =>
      (await api.patch<User>(`/api/v1/users/${id}`, body)).data,
    onSuccess: () => {
      notifications.show({ message: "Пользователь обновлён", color: "green" });
      void qc.invalidateQueries({ queryKey: ["users"] });
      setEditingUser(undefined);
    },
    onError: (e) => notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  // delete
  const [confirmDeleteUser, setConfirmDeleteUser] = useState<User | null>(null);
  const deleteMut = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/api/v1/users/${id}`)).data,
    onSuccess: () => {
      notifications.show({ message: "Пользователь деактивирован", color: "green" });
      void qc.invalidateQueries({ queryKey: ["users"] });
      setConfirmDeleteUser(null);
    },
    onError: (e) => notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  // password reset
  const [resetUser, setResetUser] = useState<User | null>(null);
  const resetForm = useForm({
    initialValues: { new_password: "" },
    validate: { new_password: (v) => (v.length >= 8 ? null : "Минимум 8") },
  });
  const resetMut = useMutation({
    mutationFn: async (vars: { id: string; new_password: string }) =>
      (await api.post(`/api/v1/users/${vars.id}/password`, { new_password: vars.new_password })).data,
    onSuccess: () => {
      notifications.show({ message: "Пароль сброшен", color: "green" });
      setResetUser(null);
      resetForm.reset();
    },
    onError: (e) => notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const submit = () => {
    if (!formApi) return;
    if (formApi.validate().hasErrors) return;
    const v = formApi.values as UserFormValues;
    if (editingUser) {
      const body: UpdateUserRequest = {
        full_name: v.full_name,
        email: v.email || undefined,
        phone: v.phone || undefined,
        role_code: v.role_code,
        active: v.active,
      };
      updateMut.mutate({ id: editingUser.id, body });
    } else {
      const body: CreateUserRequest = {
        login: v.login,
        password: v.password,
        full_name: v.full_name,
        email: v.email || undefined,
        phone: v.phone || undefined,
        role_code: v.role_code,
        active: v.active,
      };
      createMut.mutate(body);
    }
  };

  const columns: DataTableColumn<User>[] = [
    { key: "login", header: "Логин", render: (u) => u.login, width: 140 },
    { key: "name", header: "ФИО", render: (u) => u.full_name },
    { key: "role", header: "Роль", render: (u) => <Badge variant="light">{u.role_name}</Badge>, width: 140 },
    { key: "email", header: "Email", render: (u) => u.email ?? "—" },
    {
      key: "active",
      header: "Статус",
      render: (u) => (u.active ? <Badge color="green">Активен</Badge> : <Badge color="gray">Неактивен</Badge>),
      width: 110,
    },
    {
      key: "actions",
      header: "",
      width: 60,
      render: (u) => (
        <Menu shadow="md" position="bottom-end">
          <Menu.Target>
            <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
              <IconDots size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => setEditingUser(u)}>Редактировать</Menu.Item>
            <Menu.Item leftSection={<IconKey size={14} />} onClick={() => setResetUser(u)}>Сбросить пароль</Menu.Item>
            <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={() => setConfirmDeleteUser(u)}>
              Деактивировать
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      ),
    },
  ];

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Пользователи</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setEditingUser(null)}>
          Создать пользователя
        </Button>
      </Group>

      <TextInput
        placeholder="Поиск по логину, ФИО, email..."
        value={search}
        onChange={(e) => { setSearch(e.currentTarget.value); setPage(1); }}
        style={{ maxWidth: 400 }}
      />

      <DataTable
        columns={columns}
        rows={data?.items}
        loading={isLoading}
        total={data?.total ?? 0}
        page={page}
        pageSize={50}
        onPageChange={setPage}
        rowKey={(u) => u.id}
      />

      <FormModal
        opened={isFormOpen}
        onClose={() => setEditingUser(undefined)}
        title={editingUser ? "Редактировать пользователя" : "Создать пользователя"}
        onSubmit={submit}
        loading={createMut.isPending || updateMut.isPending}
      >
        <UserForm initial={editingUser ?? undefined} onChange={setFormApi} />
      </FormModal>

      {resetUser && (
        <FormModal
          opened={true}
          onClose={() => { setResetUser(null); resetForm.reset(); }}
          title={`Сбросить пароль для ${resetUser.login}`}
          onSubmit={() => {
            if (resetForm.validate().hasErrors) return;
            resetMut.mutate({ id: resetUser.id, new_password: resetForm.values.new_password });
          }}
          loading={resetMut.isPending}
          submitLabel="Сбросить"
        >
          <PasswordInput
            label="Новый пароль (минимум 8 символов)"
            required
            {...resetForm.getInputProps("new_password")}
          />
        </FormModal>
      )}

      <ConfirmDialog
        opened={!!confirmDeleteUser}
        onClose={() => setConfirmDeleteUser(null)}
        onConfirm={() => confirmDeleteUser && deleteMut.mutate(confirmDeleteUser.id)}
        title="Деактивировать пользователя?"
        message={`Пользователь ${confirmDeleteUser?.login} больше не сможет войти в систему. Запись останется в БД.`}
        confirmLabel="Деактивировать"
        loading={deleteMut.isPending}
      />
    </Stack>
  );
}
